enum VideoProvider {
    YOUTUBE = 'youtube',
    VIMEO = 'vimeo',
}

type VideoLink = {
    provider: VideoProvider;
    id: string;
    hash?: string;
};

const VideoHelpers = {
    parseLink: (url: string): VideoLink | null => {
        const parsed = URL.canParse(url) ? new URL(url) : null;

        if (!parsed || !['http:', 'https:'].includes(parsed.protocol)) {
            return null;
        }

        const host = parsed.hostname.replace(/^(www|m)\./, '');

        const youTubeId =
            host === 'youtu.be'
                ? parsed.pathname.split('/')[1]
                : host === 'youtube.com' || host === 'youtube-nocookie.com'
                  ? parsed.pathname === '/watch'
                      ? parsed.searchParams.get('v')
                      : parsed.pathname.match(/^\/(?:embed|shorts|live)\/([^/]+)/)?.[1]
                  : null;

        if (youTubeId && /^[a-zA-Z0-9_-]{11}$/.test(youTubeId)) {
            return { provider: VideoProvider.YOUTUBE, id: youTubeId };
        }

        const vimeoMatch =
            (host === 'vimeo.com' || host === 'player.vimeo.com') &&
            parsed.pathname.match(/^\/(?:video\/)?(\d+)(?:\/([a-f0-9]+))?(?:\/|$)/);

        if (vimeoMatch) {
            return { provider: VideoProvider.VIMEO, id: vimeoMatch[1], hash: vimeoMatch[2] };
        }

        return null;
    },

    providerName: (provider: VideoProvider) => (provider === VideoProvider.YOUTUBE ? 'YouTube' : 'Vimeo'),

    embedUrl: (link: VideoLink) =>
        link.provider === VideoProvider.YOUTUBE
            ? `https://www.youtube-nocookie.com/embed/${link.id}?rel=0&modestbranding=1&playsinline=1`
            : `https://player.vimeo.com/video/${link.id}?${link.hash ? `h=${link.hash}&` : ''}dnt=1`,

    watchUrl: (link: VideoLink) =>
        link.provider === VideoProvider.YOUTUBE
            ? `https://www.youtube.com/watch?v=${link.id}`
            : `https://vimeo.com/${link.id}${link.hash ? `/${link.hash}` : ''}`,

    oEmbedUrl: (link: VideoLink) =>
        link.provider === VideoProvider.YOUTUBE
            ? `https://www.youtube.com/oembed?format=json&url=${encodeURIComponent(VideoHelpers.watchUrl(link))}`
            : `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(VideoHelpers.watchUrl(link))}`,
};

export type { VideoLink };
export { VideoHelpers };
